import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { loadRoutes, saveRoutes, loadSettings, saveSettings } from '../utils/storage';
import { decodeRouteFromHash } from '../utils/share';
import { loadSharedRouteFromFirestore } from '../services/firestoreRoutes';
import { fetchElevationSegments, getCachedElevation, cacheElevation } from '../utils/elevation';
import { fetchCloudSavedRoutes, syncCloudSavedRoutes } from '../services/savedRoutesService';
import { useAuth } from './AuthContext';

const RouteContext = createContext(null);

const DEFAULT_ROUTE = {
  id: null,
  name: 'My Route',
  waypoints: [],
  geometry: null,
  distance: null,
  duration: null,
  // Per-leg breakdown (index i = waypoints[i] → waypoints[i+1]), when the routing
  // engine that answered exposes it. Lets a single dragged waypoint re-route only
  // its two adjacent legs instead of the whole path — see UPDATE_WAYPOINT below.
  legs: null,
};

const DEFAULT_AVOID = { motorway: false, toll: false, ferry: false };

function createInitialState() {
  const settings = loadSettings();
  return {
    current: { ...DEFAULT_ROUTE },
    saved: loadRoutes(),
    // Motoroam is motorcycle-only — routing always uses the driving (road) profile.
    profile: 'driving',
    avoidOptions: settings.avoidOptions || { ...DEFAULT_AVOID },
    curvy: settings.curvy || false,   // Pro: curvy-optimized routing
    pois: null,                       // { fuel, viewpoint, food } for current route
    poiPins: { fuel: false, viewpoint: false, food: false, discoveries: true }, // which categories show on map
    discoveries: null,  // Wikipedia "worth a stop" places along the current route
    // Which POI/discovery a list click last focused, so its map marker can be
    // highlighted: { kind: 'poi', cat, i } | { kind: 'discovery', id } | null.
    selectedMapItem: null,
    isRouting: false,
    routeError: null,
    toast: null,
    loopVariants: null, // { variants: [{id, color, viaPoints, geometry, distance, duration, overlap}], selectedId }
    elevation: null,    // { segments, points, minEle, maxEle, ascent, descent } for the current route
    mapFocus: 0,        // bumped when a route loads → UI minimizes pages + bottom sheet
  };
}

// Loading a route should reveal the map: close open pages and minimize the sheet.
const MAP_FOCUS_ACTIONS = new Set(['LOAD_ROUTE', 'LOAD_COMMUNITY_ROUTE']);

// Any action that changes waypoints invalidates the generated loop alternatives.
const VARIANT_CLEARING_ACTIONS = new Set([
  'ADD_WAYPOINT', 'REMOVE_WAYPOINT', 'UPDATE_WAYPOINT', 'INSERT_WAYPOINT',
  'MOVE_WAYPOINT_UP', 'MOVE_WAYPOINT_DOWN', 'SET_START', 'SET_BASE_WAYPOINTS',
  'CLEAR_LOOP', 'CLEAR_ROUTE', 'LOAD_ROUTE', 'SAVE_ROUTE',
]);

function reducer(state, action) {
  let next = baseReducer(state, action);
  if (next.loopVariants && VARIANT_CLEARING_ACTIONS.has(action.type)) {
    next = { ...next, loopVariants: null };
  }
  // A fresh/cleared route invalidates whatever POI/discovery marker was
  // highlighted — it belonged to the previous route's list.
  if (next.selectedMapItem && (action.type === 'CLEAR_ROUTE' || action.type === 'LOAD_ROUTE')) {
    next = { ...next, selectedMapItem: null };
  }
  if (MAP_FOCUS_ACTIONS.has(action.type)) {
    next = { ...next, mapFocus: (next.mapFocus || 0) + 1 };
  }
  return next;
}

// A new waypoint dropped into the middle of the route falls inside exactly one
// existing leg — split just that leg into two stale (null) ones at the insertion
// point and carry every other already-computed leg over untouched, so placing a
// point only re-routes its two new legs instead of the whole path. Returns null
// if `legs` isn't available/aligned for `waypointsLenBefore` (caller falls back
// to a full recompute).
function insertLegSplit(legs, waypointsLenBefore, index) {
  if (!legs || legs.length !== waypointsLenBefore - 1) return null;
  if (index <= 0 || index >= waypointsLenBefore) {
    // Inserted at the very start/end — no existing leg to split, just one new one.
    const next = legs.slice();
    if (index <= 0) next.unshift(null); else next.push(null);
    return next;
  }
  return [...legs.slice(0, index - 1), null, null, ...legs.slice(index)];
}

function baseReducer(state, action) {
  switch (action.type) {
    case 'ADD_WAYPOINT': {
      // Insert before any loop-via / loop-end waypoints so user stops
      // always come before the generated loop section.
      const wps = [...state.current.waypoints];
      let insertAt = wps.length;
      for (let i = wps.length - 1; i >= 0; i--) {
        if (!wps[i].isLoopVia && !wps[i].isLoopEnd) { insertAt = i + 1; break; }
      }
      const legs = insertLegSplit(state.current.legs, wps.length, insertAt);
      wps.splice(insertAt, 0, action.payload);
      if (legs) {
        return { ...state, current: { ...state.current, waypoints: wps, legs }, routeError: null };
      }
      return {
        ...state,
        current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null },
        routeError: null,
      };
    }

    case 'UPDATE_WAYPOINT': {
      const { index } = action.payload;
      const wps = state.current.waypoints.map((wp, i) =>
        i === index
          ? { ...wp, lat: action.payload.lat, lng: action.payload.lng,
              ...(action.payload.name !== undefined ? { name: action.payload.name } : {}) }
          : wp
      );
      // Waypoint count/order is unchanged, so any previously-known legs are still
      // correctly aligned — invalidate only the (up to) two legs touching this
      // point instead of the whole route, so useRouting only re-fetches those.
      const prevLegs = state.current.legs;
      const legsAligned = prevLegs && prevLegs.length === state.current.waypoints.length - 1;
      if (legsAligned) {
        const legs = prevLegs.slice();
        if (index > 0) legs[index - 1] = null;
        if (index < legs.length) legs[index] = null;
        return { ...state, current: { ...state.current, waypoints: wps, legs } };
      }
      return { ...state, current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null } };
    }

    case 'INSERT_WAYPOINT': {
      const wps = [...state.current.waypoints];
      const legs = insertLegSplit(state.current.legs, wps.length, action.payload.index);
      wps.splice(action.payload.index, 0, {
        lat: action.payload.lat, lng: action.payload.lng,
        name: action.payload.name || '', isLoopVia: false, isLoopEnd: false,
      });
      if (legs) {
        return { ...state, current: { ...state.current, waypoints: wps, legs } };
      }
      return { ...state, current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null } };
    }

    case 'REMOVE_WAYPOINT': {
      const waypoints = state.current.waypoints.filter((_, i) => i !== action.payload);
      return {
        ...state,
        current: { ...state.current, waypoints, geometry: null, distance: null, duration: null, legs: null },
      };
    }

    case 'MOVE_WAYPOINT_UP': {
      const i = action.payload;
      if (i === 0) return state;
      const wps = [...state.current.waypoints];
      [wps[i - 1], wps[i]] = [wps[i], wps[i - 1]];
      return {
        ...state,
        current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null },
      };
    }

    case 'MOVE_WAYPOINT_DOWN': {
      const i = action.payload;
      if (i >= state.current.waypoints.length - 1) return state;
      const wps = [...state.current.waypoints];
      [wps[i], wps[i + 1]] = [wps[i + 1], wps[i]];
      return {
        ...state,
        current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null },
      };
    }

    case 'UPDATE_WAYPOINT_NAME':
      return {
        ...state,
        current: {
          ...state.current,
          // Clears `pending` too — a resolved name means whatever was still
          // loading for this waypoint (see ADD_WAYPOINT's optimistic add) is done.
          waypoints: state.current.waypoints.map((wp, i) =>
            i === action.payload.index ? { ...wp, name: action.payload.name, pending: false } : wp
          ),
        },
      };

    case 'SET_GEOMETRY':
      return {
        ...state,
        current: {
          ...state.current,
          geometry: action.payload.geometry,
          distance: action.payload.distance,
          duration: action.payload.duration,
          legs: action.payload.legs ?? null,
        },
        routeError: null,
      };

    case 'SET_ELEVATION':
      return { ...state, elevation: action.payload };

    case 'SET_ROUTE_NAME':
      return { ...state, current: { ...state.current, name: action.payload } };

    case 'SET_ROUTING':
      return { ...state, isRouting: action.payload };

    case 'SET_ROUTE_ERROR':
      return { ...state, routeError: action.payload, isRouting: false };

    case 'SET_PROFILE':
      return {
        ...state,
        profile: action.payload,
        current: { ...state.current, geometry: null, distance: null, duration: null, legs: null },
      };

    case 'TOGGLE_AVOID':
      return {
        ...state,
        avoidOptions: {
          ...state.avoidOptions,
          [action.payload]: !state.avoidOptions[action.payload],
        },
        current: { ...state.current, geometry: null, distance: null, duration: null, legs: null },
      };

    case 'SET_CURVY':
      // Clear geometry so the route recomputes with the new routing engine.
      return {
        ...state,
        curvy: action.payload,
        current: { ...state.current, geometry: null, distance: null, duration: null, legs: null },
      };

    case 'SET_POIS':
      return { ...state, pois: action.payload };

    case 'SET_DISCOVERIES':
      return { ...state, discoveries: action.payload };

    case 'TOGGLE_POI_PIN':
      return {
        ...state,
        poiPins: { ...state.poiPins, [action.payload]: !state.poiPins[action.payload] },
      };

    case 'SET_SELECTED_MAP_ITEM':
      return { ...state, selectedMapItem: action.payload };

    case 'SET_START': {
      const wps = [...state.current.waypoints];
      if (wps.length === 0) wps.push(action.payload);
      else wps[0] = action.payload;
      return {
        ...state,
        current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null },
        routeError: null,
      };
    }

    case 'SET_END': {
      const wps = [...state.current.waypoints];
      if (wps.length <= 1) wps.push(action.payload);
      else wps[wps.length - 1] = action.payload;
      return {
        ...state,
        current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null },
        routeError: null,
      };
    }

    case 'SWAP_ENDPOINTS': {
      const wps = [...state.current.waypoints];
      if (wps.length < 2) return state;
      const first = wps[0];
      wps[0] = wps[wps.length - 1];
      wps[wps.length - 1] = first;
      return {
        ...state,
        current: { ...state.current, waypoints: wps, geometry: null, distance: null, duration: null, legs: null },
      };
    }

    case 'SAVE_ROUTE': {
      // legs carries through so reloading this route later can still re-route a
      // single dragged waypoint instead of the whole path on the very first edit.
      const route = {
        ...state.current,
        // Drops any lingering `pending` flag from a map-clicked waypoint whose
        // snap/name lookup hadn't resolved yet — reloading later would never
        // clear it, leaving a stop stuck showing "Locating…" forever.
        waypoints: state.current.waypoints.map(wp => (wp.pending ? { ...wp, pending: false } : wp)),
        id: String(Date.now()),   // always a new ID so each save creates a distinct entry
        savedAt: new Date().toISOString(),
      };
      return { ...state, current: route, saved: [route, ...state.saved] };
    }

    case 'SET_SAVED':
      return { ...state, saved: action.payload };

    case 'LOAD_ROUTE':
      return {
        ...state,
        current: { ...action.payload },
        routeError: null,
      };

    case 'MARK_PUBLISHED': {
      const saved = state.saved.map(r =>
        r.id === action.payload.routeId ? { ...r, publishedId: action.payload.publishedId } : r
      );
      return { ...state, saved };
    }

    case 'CLEAR_PUBLISHED': {
      const saved = state.saved.map(r =>
        r.publishedId === action.payload ? { ...r, publishedId: null } : r
      );
      return { ...state, saved };
    }

    case 'DELETE_ROUTE': {
      const saved = state.saved.filter(r => r.id !== action.payload);
      const current =
        state.current.id === action.payload ? { ...DEFAULT_ROUTE } : state.current;
      return { ...state, saved, current };
    }

    case 'SET_WAYPOINTS':
      return {
        ...state,
        current: {
          ...DEFAULT_ROUTE,
          name: action.payload.name ?? 'Loop Route',
          waypoints: action.payload.waypoints,
        },
        routeError: null,
      };

    case 'SET_BASE_WAYPOINTS': {
      // Replace all waypoints with the provided base wps (no loop flags).
      // Any existing loop is cleared because loop-via/end wps are not included.
      return {
        ...state,
        current: { ...state.current, waypoints: action.payload, geometry: null, distance: null, duration: null, legs: null },
        routeError: null,
      };
    }

    case 'GENERATE_LOOP': {
      const baseWps = state.current.waypoints.filter(wp => !wp.isLoopVia && !wp.isLoopEnd);
      if (!baseWps.length) return state;
      const startWp = baseWps[0];
      const newWps = [
        ...baseWps,
        ...action.payload.viaPoints.map(vp => ({ lat: vp.lat, lng: vp.lng, name: vp.name || 'Via', isLoopVia: true })),
        { lat: startWp.lat, lng: startWp.lng, name: startWp.name || 'Start / Finish', isLoopEnd: true },
      ];
      // If geometry is provided (progressive routing pre-computed it), set it so
      // useRouting sees geometry !== null and skips re-routing.
      return {
        ...state,
        current: {
          ...state.current,
          waypoints: newWps,
          geometry:  action.payload.geometry  ?? null,
          distance:  action.payload.distance  ?? null,
          duration:  action.payload.duration  ?? null,
          legs: null,
        },
        routeError: null,
      };
    }

    case 'SET_LOOP_VARIANTS':
      return { ...state, loopVariants: action.payload };

    case 'APPLY_LOOP_VARIANT': {
      const v = action.payload;
      const baseWps = state.current.waypoints.filter(wp => !wp.isLoopVia && !wp.isLoopEnd);
      if (!baseWps.length) return state;
      const startWp = baseWps[0];
      const newWps = [
        ...baseWps,
        ...v.viaPoints.map(vp => ({ lat: vp.lat, lng: vp.lng, name: vp.name || 'Via', bearing: vp.bearing, isLoopVia: true })),
        { lat: startWp.lat, lng: startWp.lng, name: startWp.name || 'Start / Finish', isLoopEnd: true },
      ];
      return {
        ...state,
        current: {
          ...state.current,
          waypoints: newWps,
          geometry:  v.geometry,
          distance:  v.distance,
          duration:  v.duration,
          legs: null,
        },
        loopVariants: state.loopVariants
          ? { ...state.loopVariants, selectedId: v.id }
          : null,
        routeError: null,
      };
    }

    case 'APPLY_DEST_VARIANT': {
      // Open route: commit a generated destination as a normal (saveable) stop.
      // isGenDest tags it so re-applying another option replaces it instead of
      // stacking. Geometry is set directly so useRouting skips a re-route flash.
      const v = action.payload;
      const baseWps = state.current.waypoints
        .filter(wp => !wp.isLoopVia && !wp.isLoopEnd && !wp.isGenDest);
      if (!baseWps.length) return state;
      const dest = {
        lat: v.destination.lat, lng: v.destination.lng,
        name: v.destination.name || 'Destination', isGenDest: true,
      };
      return {
        ...state,
        current: {
          ...state.current,
          waypoints: [...baseWps, dest],
          geometry: v.geometry, distance: v.distance, duration: v.duration,
          legs: null,
        },
        loopVariants: state.loopVariants
          ? { ...state.loopVariants, selectedId: v.id }
          : null,
        routeError: null,
      };
    }

    case 'APPLY_RETURN_VARIANT': {
      // Commit a chosen "route back home" leg: the full round-trip (outbound +
      // return) is precomputed in the payload's _payload, built from the
      // outbound snapshot so switching returns never stacks.
      const p = action.payload._payload || action.payload;
      return {
        ...state,
        current: {
          ...state.current,
          waypoints: p.waypoints,
          geometry: p.geometry, distance: p.distance, duration: p.duration,
          legs: null,
        },
        loopVariants: state.loopVariants
          ? { ...state.loopVariants, selectedId: action.payload.id ?? p.id }
          : null,
        routeError: null,
      };
    }

    case 'CLEAR_LOOP': {
      const baseWps = state.current.waypoints.filter(wp => !wp.isLoopVia && !wp.isLoopEnd);
      return {
        ...state,
        current: { ...state.current, waypoints: baseWps, geometry: null, distance: null, duration: null, legs: null },
      };
    }

    case 'CLEAR_ROUTE':
      return { ...state, current: { ...DEFAULT_ROUTE }, routeError: null };

    case 'SHOW_TOAST':
      return { ...state, toast: action.payload };

    case 'HIDE_TOAST':
      return { ...state, toast: null };

    default:
      return state;
  }
}

export function RouteProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, null, createInitialState);
  const { user } = useAuth();

  // Latest state without re-triggering the login-merge effect on every change.
  const stateRef = useRef(state);
  stateRef.current = state;
  const cloudSyncedRef = useRef(false);
  const syncedUidRef = useRef(null);

  // On sign-in: merge local saved routes with the cloud copy (so nothing saved
  // while logged out is lost), make the merged set the source of truth, and
  // push it back up. On sign-out, stop syncing (local cache remains).
  useEffect(() => {
    if (!user) { cloudSyncedRef.current = false; syncedUidRef.current = null; return; }
    if (syncedUidRef.current === user.uid) return; // already merged this session
    syncedUidRef.current = user.uid;
    let cancelled = false;
    (async () => {
      try {
        const cloud = await fetchCloudSavedRoutes(user.uid);
        const byId = new Map();
        for (const r of cloud) byId.set(String(r.id), r);
        for (const r of stateRef.current.saved) if (!byId.has(String(r.id))) byId.set(String(r.id), r);
        const merged = [...byId.values()].sort((a, b) => (b.savedAt || '').localeCompare(a.savedAt || ''));
        if (cancelled) return;
        dispatch({ type: 'SET_SAVED', payload: merged });
        cloudSyncedRef.current = true;
        await syncCloudSavedRoutes(user.uid, merged).catch(() => {});
      } catch { /* offline / rules — keep local */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Persist saved routes locally (offline cache) and, once signed in, mirror to
  // the cloud so they sync across devices.
  useEffect(() => {
    saveRoutes(state.saved);
    if (user && cloudSyncedRef.current) {
      syncCloudSavedRoutes(user.uid, state.saved).catch(() => {});
    }
  }, [state.saved, user]);

  // Persist settings
  useEffect(() => {
    saveSettings({ profile: state.profile, avoidOptions: state.avoidOptions, curvy: state.curvy });
  }, [state.profile, state.avoidOptions, state.curvy]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!state.toast) return;
    const id = setTimeout(() => dispatch({ type: 'HIDE_TOAST' }), 3000);
    return () => clearTimeout(id);
  }, [state.toast]);

  // Fetch elevation whenever the route geometry changes. Shared by the map
  // (grade-coloured path) and the sidebar (elevation graph) — one fetch.
  const elevKeyRef = useRef(null);
  useEffect(() => {
    const coords = state.current.geometry?.coordinates;
    if (!coords || coords.length < 3) {
      elevKeyRef.current = null;
      dispatch({ type: 'SET_ELEVATION', payload: null });
      return;
    }
    const key = `${coords.length}:${coords[0]}:${coords[coords.length - 1]}`;
    if (key === elevKeyRef.current) return; // same route — keep current elevation
    elevKeyRef.current = key;

    // Serve from cache instantly when we've measured this geometry before —
    // no flicker, and resilient to the elevation API's daily rate limit.
    const cached = getCachedElevation(key);
    if (cached) { dispatch({ type: 'SET_ELEVATION', payload: cached }); return; }

    dispatch({ type: 'SET_ELEVATION', payload: null });
    const ctl = new AbortController();
    const t = setTimeout(() => {
      fetchElevationSegments(coords, ctl.signal)
        .then(res => {
          if (ctl.signal.aborted) return;
          if (res) cacheElevation(key, res);
          dispatch({ type: 'SET_ELEVATION', payload: res });
        })
        .catch(() => {});
    }, 350);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [state.current.geometry]);

  // Load shared route on mount — checks ?share=<firestoreId> first, then legacy #route=<base64>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareId = params.get('share');

    if (shareId) {
      loadSharedRouteFromFirestore(shareId)
        .then(route => {
          if (route) dispatch({ type: 'LOAD_ROUTE', payload: route });
          window.history.replaceState(null, '', window.location.pathname);
        })
        .catch(() => {
          window.history.replaceState(null, '', window.location.pathname);
        });
      return;
    }

    // Legacy base64 hash share
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('route=')) {
      const route = decodeRouteFromHash(hash.slice(6));
      if (route) {
        dispatch({ type: 'LOAD_ROUTE', payload: route });
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    dispatch({ type: 'SHOW_TOAST', payload: { message, type } });
  }, []);

  return (
    <RouteContext.Provider value={{ state, dispatch, showToast }}>
      {children}
    </RouteContext.Provider>
  );
}

export function useRoute() {
  const ctx = useContext(RouteContext);
  if (!ctx) throw new Error('useRoute must be used inside <RouteProvider>');
  return ctx;
}
